"use client";

import { useEffect, useMemo, useState } from "react";
import type { HubUpdateDto } from "@cede/types";
import { METRICS_EVENTS, MetricsEvents } from "@cede/types";
import { HubUpdatesContent } from "./HubUpdatesContent";
import { finishPlace, FinishPlace, StepTrackingData } from "@/components/shared/StepperModal";
import { PortalModal } from "@/components/ui/portal-modal";
import { SkeletonElement } from "@/components/ui/skeleton";
import { useHubUpdates } from "@/hooks";
import { useDevice } from "@/hooks";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { cn } from "@/lib/utils";
import { useHubUpdatesStore } from "@/store/hubUpdatesStore";
import { forgeCmsMediaUrl } from "@/utils/cms";
import { enqueueSnackbar } from "notistack";

interface HubUpdatesModalProps {
  open: boolean;
  onClose: () => void;
}

export const HubUpdatesModal = ({ open, onClose }: HubUpdatesModalProps) => {
  const { isMobile } = useDevice();
  const { trackEvent } = useMetricsEvents();
  // Always fetch updates to determine if modal should auto-show
  const { updates, latestPublishedAt, isLoading } = useHubUpdates();
  const { hasSeenUpdate, markLatestUpdateAsSeen } = useHubUpdatesStore();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Reset to first step when modal opens
  useEffect(() => {
    if (open) {
      setCurrentStepIndex(0);
    }
  }, [open]);

  // Filter out updates that have already been seen (already sorted by ID desc from hook)
  const unseenUpdates = useMemo(() => {
    if (!updates || isLoading) return [];

    return updates.filter((update) => {
      if (!update.publishedAt) return true;
      return !hasSeenUpdate(new Date(update.publishedAt).toISOString());
    });
  }, [updates, isLoading, hasSeenUpdate]);

  const getStepTrackingData = (update: HubUpdateDto): StepTrackingData => ({
    stepNumber: unseenUpdates.indexOf(update) + 1,
    title: update.title,
    updateId: update.id,
  });

  // Don't render if no unseen updates and not loading
  if ((!unseenUpdates || unseenUpdates.length === 0) && !isLoading) {
    return null;
  }

  const currentStep = unseenUpdates[currentStepIndex];
  const totalSteps = unseenUpdates.length;
  const isLastStep = totalSteps > 0 ? currentStepIndex === totalSteps - 1 : false;
  const isFirstStep = currentStepIndex === 0;

  const trackStepEvent = (event: MetricsEvents, step?: HubUpdateDto, extra?: Record<string, unknown>) => {
    if (!step) return;
    const { stepNumber, title, ...other } = getStepTrackingData(step);
    trackEvent(event, {
      trackingPrefix: "Hub Updates",
      currentStep: stepNumber,
      currentStepTitle: title,
      ...other,
      ...extra,
    });
  };

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStepIndex((prev) => prev + 1);
    }
    trackStepEvent(METRICS_EVENTS.STEPPER_MODAL_NEXT, currentStep);
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStepIndex((prev) => prev - 1);
    }
    trackStepEvent(METRICS_EVENTS.STEPPER_MODAL_BACK, currentStep);
  };

  const handleFinish = (place: FinishPlace) => {
    trackStepEvent(METRICS_EVENTS.STEPPER_MODAL_CLOSE, currentStep, { place });
    if (latestPublishedAt) {
      markLatestUpdateAsSeen(latestPublishedAt);
    }
    onClose();
  };

  const handleCloseAttempt = () => {
    enqueueSnackbar("Please use the Close button in the last update step.", {
      variant: "info",
    });
  };

  const preventClose = true;

  return (
    <PortalModal
      open={open}
      onOpenChange={() => handleFinish(finishPlace.OUTSIDE_MODAL)}
      disableBackdropClick={preventClose}
      disableEscapeKey={preventClose}
      onCloseAttempt={handleCloseAttempt}
      showCloseButton={false}
      className={cn("!py-0 gap-0 z-modal-high", isMobile ? "w-full h-[90vh]" : "w-auto")}
      style={{
        width: isMobile ? "100%" : "1100px",
        maxWidth: isMobile ? "100%" : "1100px",
        maxHeight: isMobile ? "90vh" : "700px",
      }}
    >
      <div className={cn("flex flex-col h-full", "focus:outline-none", isMobile && "rounded-b-none")}>
        {isLoading || !currentStep ? (
          <div className="flex-1 flex items-center justify-center">
            <SkeletonElement className="w-16 h-16 rounded-full" />
          </div>
        ) : (
          <div
            className="flex-1 relative"
            style={isMobile ? { flex: "1 1 auto", minHeight: 0 } : { height: "600px", minHeight: "600px" }}
          >
            {unseenUpdates.map((update, index) => {
              const publishedAtIso = update.publishedAt ? new Date(update.publishedAt).toISOString() : undefined;

              return (
                <HubUpdatesContent
                  key={update.id}
                  title={update.title}
                  description={update.description}
                  imageUrl={forgeCmsMediaUrl(update.imageUrl) ?? undefined}
                  link={update.link ?? undefined}
                  publishedAt={publishedAtIso}
                  onClose={handleFinish}
                  isActive={index === currentStepIndex}
                  index={index}
                  currentIndex={currentStepIndex}
                  totalSteps={unseenUpdates.length}
                  onNext={handleNext}
                  onBack={handleBack}
                />
              );
            })}
          </div>
        )}
      </div>
    </PortalModal>
  );
};
